import React, { useMemo, useState } from "react";
import { formatRange, formatDate, getClassLabel, getDisplayName } from "../utils/actionUtils";

const getFormValue = <T = string,>(
  form: any,
  keys: string[],
  fallback: T = "" as T,
): T => {
  if (!form) return fallback;

  for (const key of keys) {
    if (form[key] !== undefined && form[key] !== null) return form[key] as T;
  }

  return fallback;
};

const isPresent = (value: unknown) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const stringifyValue = (value: unknown) => {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.filter(isPresent).join(", ");
  return String(value);
};

const toBooleanValue = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "yes", "ja", "1", "on"].includes(normalized)) return true;
    if (["false", "no", "nee", "0", "off", ""].includes(normalized)) return false;
    // Older UI versions allowed free text such as A/B/C.
    // The backend DTO expects a Boolean, so any non-empty legacy value means: has a swimming diploma.
    return true;
  }
  return false;
};

const formatArrayDate = (value: unknown) => {
  if (!Array.isArray(value) || value.length < 3) return "";

  const [year, month, day] = value;
  if (!year || !month || !day) return "";

  return formatDate(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
};

const getRequestValue = (request: any, keys: string[], fallback = "") => {
  for (const key of keys) {
    if (isPresent(request?.[key])) return stringifyValue(request[key]);
  }

  return fallback;
};

const InlineTripPanel = ({
  t,
  request,
  form,
  onChange,
  onSubmit,
  onCancel,
  submitting,
  error,
}: any) => {
  const initialConsent = Boolean(
    getFormValue<boolean>(
      form,
      ["consent", "consented", "consentGiven", "approved", "permissionGranted", "tripConsent"],
      false,
    ),
  );
  const [consentChecked, setConsentChecked] = useState(initialConsent);

  const childName =
    request?.childName ||
    getDisplayName(request?.child) ||
    t("common.unknown_child", { defaultValue: "Child" });
  const outingTitle =
    request?.outingName ||
    request?.label ||
    t("parentPortal.trip_consent.card_title_short", {
      defaultValue: "Trip consent",
    });
  const classLabel =
    getClassLabel(request) ||
    request?.classroomName ||
    request?.className ||
    "";
  const range = formatRange(request?.startsAt, request?.expiresAt);
  const expiresAt = formatDate(request?.expiresAt);
  const birthDate = formatArrayDate(request?.childDateBirth) || formatDate(request?.dateOfBirth);

  const phoneKey =
    form?.emergencyPhone !== undefined
      ? "emergencyPhone"
      : form?.emergencyContactPhone !== undefined
        ? "emergencyContactPhone"
        : form?.phone !== undefined
          ? "phone"
          : "emergencyPhone";
  const [phoneValue, setPhoneValue] = useState(
    String(
      getFormValue(
        form,
        ["emergencyPhone", "emergencyContactPhone", "phone"],
        getRequestValue(request, ["emergencyPhone", "parentPhones", "phone"], ""),
      ),
    ),
  );

  const [emergencyContactValue, setEmergencyContactValue] = useState(
    String(getFormValue(form, ["emergencyContact"], getRequestValue(request, ["emergencyContact"], ""))),
  );
  const [doctorNameValue, setDoctorNameValue] = useState(
    String(getFormValue(form, ["doctorName"], getRequestValue(request, ["doctorName"], ""))),
  );
  const [doctorPhoneValue, setDoctorPhoneValue] = useState(
    String(getFormValue(form, ["doctorPhone"], getRequestValue(request, ["doctorPhone"], ""))),
  );
  const [allergiesValue, setAllergiesValue] = useState(
    String(getFormValue(form, ["allergies"], getRequestValue(request, ["allergies"], ""))),
  );
  const [medicalNotesValue, setMedicalNotesValue] = useState(
    String(getFormValue(form, ["medicalNotes"], getRequestValue(request, ["medicalNotes"], ""))),
  );
  const [swimmingDiplomaValue, setSwimmingDiplomaValue] = useState(
    toBooleanValue(getFormValue(form, ["swimmingDiploma"], request?.swimmingDiploma ?? false)),
  );

  const infoRows = useMemo(
    () =>
      [
        {
          label: t("parentPortal.trip_consent.child", { defaultValue: "Child" }),
          value: childName,
        },
        {
          label: t("parentPortal.trip_consent.date_of_birth", { defaultValue: "Date of birth" }),
          value: birthDate,
        },
        {
          label: t("parentPortal.trip_consent.classroom", { defaultValue: "Class" }),
          value: classLabel,
        },
        {
          label: t("parentPortal.trip_consent.period", { defaultValue: "Period" }),
          value: range,
        },
        {
          label: t("parentPortal.trip_consent.parent_phone_numbers", {
            defaultValue: "Parent phone numbers",
          }),
          value: getRequestValue(request, ["parentPhones", "parentPhoneNumbers"], ""),
        },
        {
          label: t("parentPortal.trip_consent.consent_progress", {
            defaultValue: "Consent progress",
          }),
          value:
            request?.consentsRequired !== undefined
              ? `${request?.consentsGiven ?? 0}/${request?.consentsRequired}`
              : "",
        },
      ].filter((row) => isPresent(row.value)),
    [birthDate, childName, classLabel, range, request, t],
  );

  const handleConsentChange = (checked: boolean) => {
    setConsentChecked(checked);
    onChange?.("consent", checked);
    onChange?.("tripConsent", checked);
  };

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    key: string,
    value: string,
  ) => {
    setter(value);
    onChange?.(key, value);
  };

  const handleBooleanFieldChange = (
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    key: string,
    value: boolean,
  ) => {
    setter(value);
    onChange?.(key, value);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const cleanString = (value: string) => {
      const trimmed = String(value || "").trim();
      return trimmed === "" ? null : trimmed;
    };

    onSubmit?.(request, {
      parentNames: cleanString(String(form?.parentNames ?? request?.parentNames ?? "")),
      parentPhones: cleanString(String(form?.parentPhones ?? request?.parentPhones ?? "")),
      emergencyContact: cleanString(emergencyContactValue),
      emergencyPhone: cleanString(phoneValue),
      doctorName: cleanString(doctorNameValue),
      doctorPhone: cleanString(doctorPhoneValue),
      allergies: cleanString(allergiesValue),
      medicalNotes: cleanString(medicalNotesValue),
      swimmingDiploma: Boolean(swimmingDiplomaValue),
      tripConsent: consentChecked,
    }, { silent: true });
  };

  return (
    <form className="pp-actions-trip-panel" onSubmit={handleSubmit}>
      <div className="pp-actions-trip-hero">
        <span className="pp-actions-trip-kicker">
          {t("parentPortal.trip_consent.card_title_short", {
            defaultValue: "Trip consent",
          })}
        </span>
        <h3>{outingTitle}</h3>
        <p>{childName}</p>
        {expiresAt && (
          <span className="pp-actions-trip-expiry">
            {t("parentPortal.trip_consent.expires_on", {
              defaultValue: "Expires on {{date}}",
              date: expiresAt,
            })}
          </span>
        )}
      </div>

      <div className="pp-actions-trip-info-list">
        {infoRows.map((row) => (
          <div className="pp-actions-trip-info-row" key={row.label}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>

      <label className="pp-actions-trip-consent-card">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(event) => handleConsentChange(event.target.checked)}
        />
        <span>
          {t("parentPortal.trip_consent.consent_statement", {
            defaultValue:
              "I give permission for participation in this trip and for the use of the above information in emergencies.",
          })}
        </span>
      </label>

      <div className="pp-actions-trip-fieldset">
        <label>
          <span>
            {t("parentPortal.trip_consent.emergency_contact", {
              defaultValue: "Emergency contact",
            })}
          </span>
          <input
            type="text"
            value={emergencyContactValue}
            onChange={(event) =>
              handleFieldChange(setEmergencyContactValue, "emergencyContact", event.target.value)
            }
            placeholder={t("parentPortal.trip_consent.emergency_contact_placeholder", {
              defaultValue: "Name of emergency contact",
            })}
          />
        </label>

        <label>
          <span>
            {t("parentPortal.trip_consent.emergency_phone", {
              defaultValue: "Emergency phone number",
            })}
          </span>
          <input
            type="tel"
            value={phoneValue}
            onChange={(event) =>
              handleFieldChange(setPhoneValue, phoneKey, event.target.value)
            }
            placeholder={t(
              "parentPortal.trip_consent.emergency_phone_placeholder",
              {
                defaultValue: "Phone number",
              },
            )}
          />
        </label>

        <label>
          <span>
            {t("parentPortal.trip_consent.doctor_name", {
              defaultValue: "Doctor",
            })}
          </span>
          <input
            type="text"
            value={doctorNameValue}
            onChange={(event) =>
              handleFieldChange(setDoctorNameValue, "doctorName", event.target.value)
            }
            placeholder={t("parentPortal.trip_consent.doctor_name_placeholder", {
              defaultValue: "Doctor name",
            })}
          />
        </label>

        <label>
          <span>
            {t("parentPortal.trip_consent.doctor_phone", {
              defaultValue: "Doctor phone number",
            })}
          </span>
          <input
            type="tel"
            value={doctorPhoneValue}
            onChange={(event) =>
              handleFieldChange(setDoctorPhoneValue, "doctorPhone", event.target.value)
            }
            placeholder={t("parentPortal.trip_consent.doctor_phone_placeholder", {
              defaultValue: "Doctor phone number",
            })}
          />
        </label>

        <fieldset
          className="pp-actions-trip-radio-group pp-actions-trip-radio-group--inline"
          style={{
            border: 0,
            margin: "0",
            padding: "0",
          }}
        >
          <legend
            style={{
              display: "block",
              marginBottom: "0.65rem",
              color: "#0f172a",
              fontSize: "13px",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {t("parentPortal.trip_consent.swimming_diploma", {
              defaultValue: "Swimming diploma",
            })}
          </legend>
          <div
            className="pp-actions-trip-radio-options pp-actions-trip-radio-options--subtle"
            style={{
              alignItems: "center",
              background: "#ffffff",
              border: "1px solid #d7e0eb",
              borderRadius: "22px",
              display: "grid",
              gridTemplateColumns: "1fr 1px 1fr",
              minHeight: "3.35rem",
              overflow: "hidden",
            }}
          >
            <label
              className="pp-actions-trip-radio-option pp-actions-trip-radio-option--subtle"
              style={{
                alignItems: "center",
                cursor: submitting ? "default" : "pointer",
                display: "flex",
                gap: "0.75rem",
                height: "100%",
                padding: "0 1rem",
              }}
            >
              <input
                type="radio"
                name={`swimmingDiploma-${request?.id || request?.childId || "trip"}`}
                checked={swimmingDiplomaValue === true}
                onChange={() =>
                  handleBooleanFieldChange(setSwimmingDiplomaValue, "swimmingDiploma", true)
                }
                style={{
                  accentColor: "#2aa879",
                  height: "1.15rem",
                  margin: 0,
                  width: "1.15rem",
                }}
              />
              <span
                style={{
                  color: swimmingDiplomaValue === true ? "#0f8a68" : "#334155",
                  fontSize: "0,9rem",
                  fontWeight: swimmingDiplomaValue === true ? 650 : 500,
                }}
              >
                {t("parentPortal.trip_consent.yes", { defaultValue: "Yes" })}
              </span>
            </label>
            <span
              aria-hidden="true"
              style={{
                background: "#e6edf5",
                height: "1.8rem",
                width: "1px",
              }}
            />
            <label
              className="pp-actions-trip-radio-option pp-actions-trip-radio-option--subtle"
              style={{
                alignItems: "center",
                cursor: submitting ? "default" : "pointer",
                display: "flex",
                gap: "0.75rem",
                height: "100%",
                padding: "0 1rem",
              }}
            >
              <input
                type="radio"
                name={`swimmingDiploma-${request?.id || request?.childId || "trip"}`}
                checked={swimmingDiplomaValue === false}
                onChange={() =>
                  handleBooleanFieldChange(setSwimmingDiplomaValue, "swimmingDiploma", false)
                }
                style={{
                  accentColor: "#2aa879",
                  height: "1.15rem",
                  margin: 0,
                  width: "1.15rem",
                }}
              />
              <span
                style={{
                  color: swimmingDiplomaValue === false ? "#0f8a68" : "#334155",
                  fontSize: "0,9rem",
                  fontWeight: swimmingDiplomaValue === false ? 650 : 500,
                }}
              >
                {t("parentPortal.trip_consent.no", { defaultValue: "No" })}
              </span>
            </label>
          </div>
        </fieldset>

        <label>
          <span>
            {t("parentPortal.trip_consent.allergies", {
              defaultValue: "Allergies",
            })}
          </span>
          <textarea
            value={allergiesValue}
            onChange={(event) =>
              handleFieldChange(setAllergiesValue, "allergies", event.target.value)
            }
            placeholder={t("parentPortal.trip_consent.allergies_placeholder", {
              defaultValue: "Known allergies",
            })}
            rows={2}
          />
        </label>

        <label>
          <span>
            {t("parentPortal.trip_consent.medical_notes", {
              defaultValue: "Medical notes",
            })}
          </span>
          <textarea
            value={medicalNotesValue}
            onChange={(event) =>
              handleFieldChange(setMedicalNotesValue, "medicalNotes", event.target.value)
            }
            placeholder={t("parentPortal.trip_consent.medical_notes_placeholder", {
              defaultValue: "Medication or other medical remarks",
            })}
            rows={3}
          />
        </label>
      </div>

      {error && <p className="pp-actions-trip-error">{String(error)}</p>}

      <div className="pp-actions-trip-actions">
        <button
          type="button"
          className="pp-actions-trip-secondary"
          onClick={onCancel}
        >
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="submit"
          className="pp-actions-trip-primary"
          disabled={submitting || !consentChecked}
        >
          {submitting
            ? t("common.saving", { defaultValue: "Saving..." })
            : t("parentPortal.trip_consent.submit", {
                defaultValue: "Send consent",
              })}
        </button>
      </div>
    </form>
  );
};

export default InlineTripPanel;
